import { LinkIcon } from "@heroicons/react/24/solid";
import { useEffect, useRef, useState } from "react";
import Button from "ui/atoms/Button/Button";
import copyText from "util/copyText";
import { Player } from "./usePlayer/usePlayer";
import * as routes from "routes";
import { uuid } from "uuidv4";

interface Props {
  player: Player;
}

export default function CopyShortUrl({ player }: Props) {
  const [copied, setCopied] = useState(false);
  const copiedTimeout = useRef<NodeJS.Timeout | null>(null);
  const errorTimeout = useRef<NodeJS.Timeout | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (copied) {
      if (copiedTimeout.current) {
        clearTimeout(copiedTimeout.current);
      }

      copiedTimeout.current = setTimeout(() => {
        setCopied(false);
      }, 2000);

      return () => {
        if (copiedTimeout.current) {
          clearTimeout(copiedTimeout.current);
        }
      };
    }
  }, [copied]);

  useEffect(() => {
    if (error) {
      if (errorTimeout.current) {
        clearTimeout(errorTimeout.current);
      }

      errorTimeout.current = setTimeout(() => {
        setError(null);
      }, 2000);

      return () => {
        if (errorTimeout.current) {
          clearTimeout(errorTimeout.current);
        }
      };
    }
  }, [error]);

  const handleCopy = async () => {
    try {
      const [slug] = uuid().split("-");
      const url = routes.singing.vocalDrillerShortUrl(slug);
      const copySuccess = await copyText(url);

      if (!copySuccess) {
        setError("Failed to copy short link. Try again sorry.");

        return;
      }

      const savedLink = await player.submitShareLink(slug);

      if (savedLink) {
        setCopied(true);
      } else {
        setError("Failed to save short link. Try again sorry.");
      }
    } catch (e) {
      console.error(e);
      setError("Failed to copy short link. Try again sorry.");
    }
  };

  const successMessage = copied ? "Copied!" : null;
  const errorMessage = error ? "Unexpected error." : null;

  return (
    <Button
      theme={successMessage ? "green" : errorMessage ? "red" : "blue"}
      size="large"
      onClick={handleCopy}
      icon={(props) => <LinkIcon {...props} />}
      block
    >
      {successMessage
        ? successMessage
        : errorMessage
        ? errorMessage
        : "Copy short link"}
    </Button>
  );
}
